import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, MoreVertical, Wine, ListChecks, Bell, FileText, Copy, Edit, Trash2, ChevronDown } from 'lucide-react';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/SubscriptionAppButton';
import { Card } from '../components/SubscriptionAppCard';
import { Input } from '../components/SubscriptionAppInput';
import { DropdownMenu } from '../components/SubscriptionAppDropdownMenu';
// Mock Data
interface Template {
  id: string;
  name: string;
  description: string;
  winesCount: number;
  rulesCount: number;
  notificationsCount: number;
  lastUsed: string;
}
const mockTemplates: Template[] = [{
  id: '1',
  name: 'Reserve Release Template',
  description: 'Standard template for single-wine reserve releases to top tier members',
  winesCount: 1,
  rulesCount: 3,
  notificationsCount: 3,
  lastUsed: '2 weeks ago'
}, {
  id: '2',
  name: 'Quarterly Library Collection',
  description: 'Multi-wine library release to all active subscribers with tiered quantity limits',
  winesCount: 4,
  rulesCount: 2,
  notificationsCount: 4,
  lastUsed: '3 months ago'
}, {
  id: '3',
  name: 'Vintner Exclusive',
  description: 'Small-batch release exclusively for Vintner tier members with high per-customer limits',
  winesCount: 1,
  rulesCount: 1,
  notificationsCount: 2,
  lastUsed: '1 month ago'
}, {
  id: '4',
  name: 'New Vintage Launch',
  description: 'Broad release for all members with no tier restrictions. Used for new vintage announcements.',
  winesCount: 6,
  rulesCount: 1,
  notificationsCount: 4,
  lastUsed: '6 months ago'
}];
export function AllocationTemplatesPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  // Filter templates based on search
  const filteredTemplates = mockTemplates.filter(template => template.name.toLowerCase().includes(searchQuery.toLowerCase()) || template.description.toLowerCase().includes(searchQuery.toLowerCase()));
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-4324">
      {/* NavBar */}
      <div className="mb-6" data-id="element-4325">
        <NavBar tabs={[]} title="Allocation Templates" data-id="element-4326" />
      </div>

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-4327">
        <h1 className="text-2xl font-bold text-[#0F172A] hidden sm:block" data-id="element-4328">
          Allocation Templates
        </h1>
        <div className="flex items-center gap-3 w-full sm:w-auto justify-end" data-id="element-4329">
          <Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-4331" />} onClick={() => console.log('Create Template')} data-id="element-4330">
            Create Template
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6" data-id="element-4332">
        <div className="w-full sm:w-80" data-id="element-4333">
          <Input placeholder="Search templates..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-4335" />} data-id="element-4334" />
        </div>
        <div className="w-full sm:w-auto flex items-center gap-2" data-id="element-4336">
          <span className="text-sm text-[#64748B] whitespace-nowrap" data-id="element-4337">
            Sort by:
          </span>
          <button className="flex items-center gap-2 text-sm font-medium text-[#0F172A] bg-white border border-[#CBD5E1] rounded-md px-3 py-2 hover:bg-[#F8FAFC] transition-colors" data-id="element-4338">
            Last Used
            <ChevronDown className="w-4 h-4 text-[#64748B]" data-id="element-4339" />
          </button>
        </div>
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4340">
          {filteredTemplates.map(template => <Card key={template.id} padding="none" className="flex flex-col h-full border border-[#E2E8F0] hover:shadow-md transition-shadow duration-200" data-id="element-4341">
              <div className="p-6 flex-1 flex flex-col" data-id="element-4342">
                <div className="flex items-start justify-between gap-4 mb-2" data-id="element-4343">
                  <h3 className="text-lg font-bold text-[#0F172A] line-clamp-1" data-id="element-4344">
                    {template.name}
                  </h3>
                </div>
                <p className="text-sm text-[#64748B] line-clamp-2 mb-6 h-10" data-id="element-4345">
                  {template.description}
                </p>

                <div className="flex items-center gap-4 mb-6" data-id="element-4346">
                  <div className="flex items-center gap-1.5 text-sm text-[#475569]" title={`${template.winesCount} wines`} data-id="element-4347">
                    <Wine className="w-4 h-4 text-[#94A3B8]" data-id="element-4348" />
                    <span data-id="element-4349">{template.winesCount}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-[#475569]" title={`${template.rulesCount} rules`} data-id="element-4350">
                    <ListChecks className="w-4 h-4 text-[#94A3B8]" data-id="element-4351" />
                    <span data-id="element-4352">{template.rulesCount}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-[#475569]" title={`${template.notificationsCount} notifications enabled`} data-id="element-4353">
                    <Bell className="w-4 h-4 text-[#94A3B8]" data-id="element-4354" />
                    <span data-id="element-4355">{template.notificationsCount}</span>
                  </div>
                </div>

                <div className="mt-auto pt-4 border-t border-[#F1F5F9] flex items-center justify-between" data-id="element-4356">
                  <span className="text-xs text-[#94A3B8]" data-id="element-4357">
                    Last used: {template.lastUsed}
                  </span>
                </div>
              </div>

              <div className="p-4 bg-[#F8FAFC] border-t border-[#E2E8F0] flex items-center gap-3 rounded-b-lg" data-id="element-4358">
                <Button variant="primary" size="sm" fullWidth onClick={() => navigate('/allocations/create')} data-id="element-4359">
                  Use Template
                </Button>
                <DropdownMenu items={[{
            label: 'Edit',
            icon: <Edit className="w-4 h-4" data-id="element-4361" />,
            onClick: () => console.log('Edit', template.id)
          }, {
            label: 'Duplicate',
            icon: <Copy className="w-4 h-4" data-id="element-4362" />,
            onClick: () => console.log('Duplicate', template.id)
          }, {
            label: 'Delete',
            icon: <Trash2 className="w-4 h-4" data-id="element-4363" />,
            variant: 'danger',
            onClick: () => console.log('Delete', template.id)
          }]} data-id="element-4360" />
              </div>
            </Card>)}
        </div> /* Empty State */ : <div className="flex flex-col items-center justify-center py-16 px-4 text-center bg-white rounded-lg border border-[#E2E8F0] border-dashed" data-id="element-4364">
          <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mb-4" data-id="element-4365">
            <FileText className="w-8 h-8 text-[#94A3B8]" data-id="element-4366" />
          </div>
          <h3 className="text-lg font-semibold text-[#0F172A] mb-2" data-id="element-4367">
            No templates found
          </h3>
          <p className="text-[#64748B] max-w-md mb-6" data-id="element-4368">
            {searchQuery ? `No templates match "${searchQuery}". Try a different search term.` : 'Save your first allocation as a template to reuse it later.'}
          </p>
          {!searchQuery && <button onClick={() => navigate('/allocations/create')} className="text-[#0EA5E9] font-medium hover:text-[#0284C7] hover:underline" data-id="element-4369">
              Create Allocation
            </button>}
        </div>}
    </div>;
}